import _ from "lodash";
import React from "react";
import {
  getCurriculumProgramIdFromState,
  getInitials,
  getDataGridRandomAvatarColor,
} from "Utils";
import ACLStore from "lib/aclStore";
import {
  getAdminSetupFiltersByEntityTypeMemoized,
  ADMIN_SEARCH_TEXT_FIELDS,
  getUserCourseMapFiltersV2,
} from "Administrator/modules/Utils";
import {
  getSelectedAcademicYearId,
  getStudentQueryVariables,
  canResetStudentCodeAndFamilyInviteInSchoolRoster,
  getFilteredDividers,
  canManageSchoolRoster,
  isUserArchivalAcademicYearWiseEnabled,
  EMPTY_OBJECT,
} from "SchoolSetup/modules/Utils";
import {
  getCurriculumProgramId,
  getStaffFilter,
} from "StudentSetup/modules/StudentsModule";
import { getResetCodeMenuItems } from "ResetInviteAndSignInCodeModal/modules/ResetInviteAndSignInCodeModalUtils";
import { getTableSortOrder } from "SchoolSetup/modules/SchoolSetupModule";
import { getStudentFeedQueryV2 as getStudentFeedQuery } from "StudentSetup/modules/StudentQueries";
import { getStaffRelationFilters } from "Homeroom/modules/Selector";
import { getSelectedAcademicYear } from "modules/CommonSelectors";
import { getCourseStudentPortfolioVariables } from "StudentPortfolio/modules/StudentPortfolioUtils";
import { getCourseStudentPortfolioQuery } from "StudentPortfolio/modules/StudentPortfolioQuery";
import {
  getFamilyFeedQueryFilters,
  getFamiliesFilters,
} from "ManageFamily/modules/ManageFamilyModule";
import { getStudentFeedQueryVariablesMemoized } from "Course/routes/ClassSettingsV2/modules/ClassSettingsV2Utils";
import { disableEditForMapleBearChildOrg } from "Courses/modules/utils";
import {
  getDefaultOrderBy,
  getFormattedUserName,
} from "Utils/GetFormattedUserName";
import {
  PrintOutlined,
  DisableOutlined,
  DownloadOutlined,
  EnvelopePlainOutlined,
  ReloadArrowOutlined,
  AssessmentAddOutlined,
  EnvelopeArrowOutlined,
  LogoutOutlined,
  ManageMembersOutlined,
  ArchiveOutlined,
  MinusCircleOutlined,
  StudentAddOutlined,
} from "@toddle-edu/ds-icons";
import { Trans } from "react-i18next";
import { Alert, DatePicker } from "@toddle-edu/ds-web";
import {
  canManageClassSettings,
  canResetStudentSignInAndFamilyInviteCodesInClassSettings,
  canViewStudentSignInAndFamilyInviteCodesInClassSettings,
} from "Settings/modules/SettingsUtils";
import {
  isHomeroomEnabled,
  isStudentBlockingFeatureEnabled,
} from "Utils/ACLStoreUtils";
import moment from "moment";

export const MODULE_NAME = "STUDENT";

export const getStudentFilters = state => {
  const searchText = _.get(state, "studentSetup.searchText", "");
  const filters = _.get(state, "studentSetup.filters", {});
  const filteredCourses = _.get(state, "studentSetup.filters.courses", []);

  const isUsedForAcademicYearTransition = _.get(
    state,
    "academicYearTransition.isUsedForAcademicYearTransition",
    false
  );

  const curriculumIdSelectedFromFilter = _.get(
    filters,
    "selectedCurriculumId",
    null
  );

  let selectedCurriculumId = getCurriculumProgramId(state);

  const isAllCurriculumStudentsRoute = getIsAllStudentsRoute({
    state,
  });

  if (curriculumIdSelectedFromFilter && isAllCurriculumStudentsRoute) {
    selectedCurriculumId = curriculumIdSelectedFromFilter;
  }

  const selectedAcademicYearId = getSelectedAcademicYearId(state);

  const blockStatus = _.get(state, "studentSetup.filters.blockStatus", "ALL");

  const curriculumProgramId = isUsedForAcademicYearTransition
    ? getCurriculumProgramIdFromState({
        state,
      })
    : selectedCurriculumId;

  const filterParams = {
    type: "STUDENT",
    searchText,
    archivalState: _.get(filters, "archivalState", "ACTIVE"),
    blockStatus,
    searchTextFields: ADMIN_SEARCH_TEXT_FIELDS["student"],
    curriculumProgramId,
    yearGroups: _.get(filters, "yearGroups", []),
    academicYearId: selectedAcademicYearId,
    ...filters,
    courses: filteredCourses,
  };

  const memoizedFilters = getAdminSetupFiltersByEntityTypeMemoized(
    filterParams
  );

  return { filters: memoizedFilters };
};

export const getStudentDropDownOptionsForBulkAction = ({
  t,
  isUnblockStudentsDisabled = false,
  isCurrentAcademicYearSelected,
  isContextClassSettings = false,
  isDP,
  selectedCurriculumProgramId,
  isStudentProfileAndGradebookSummaryDownloadEnabled,
  isFutureAcademicYearSelected,
  isPortfolioDownloadDisabled,
  isFamilyInviteOptionsDisabled = false,
}) => {
  let dropDownOptions = [];

  const isPreviousAcademicYearSelected = !(
    isCurrentAcademicYearSelected || isFutureAcademicYearSelected
  );
  if (isContextClassSettings) {
    dropDownOptions = [
      {
        key: "add_to_existing_assignments",
        label: t("schoolSetup:add_students_to_existing_assignments"),
        visible: false,
        icon: <AssessmentAddOutlined />,
      },
      {
        key: "invite_family",
        label: t("common:invite_family_via_email"),
        visible:
          !isPreviousAcademicYearSelected &&
          canManageClassSettings() &&
          !disableEditForMapleBearChildOrg() &&
          !isFamilyInviteOptionsDisabled,
        icon: <EnvelopeArrowOutlined />,
      },
      {
        key: "divider-1",
        isDivider: true,
        visible: true,
      },
      {
        key: "print_student_sign_in_codes",
        label: t("common:print_with_label", {
          label: t("common:student_sign_in_code_plural"),
        }),
        visible:
          canViewStudentSignInAndFamilyInviteCodesInClassSettings() &&
          !disableEditForMapleBearChildOrg(),
        icon: <PrintOutlined />,
      },
      {
        key: "print_family_invites",
        label: t("common:print_with_label", {
          label: t("common:family_invite_plural"),
        }),
        visible:
          canViewStudentSignInAndFamilyInviteCodesInClassSettings() &&
          !disableEditForMapleBearChildOrg() &&
          !isFamilyInviteOptionsDisabled,
        icon: <PrintOutlined />,
      },
      {
        key: "divider-2",
        isDivider: true,
        visible:
          !isPreviousAcademicYearSelected &&
          canViewStudentSignInAndFamilyInviteCodesInClassSettings() &&
          canResetStudentSignInAndFamilyInviteCodesInClassSettings(),
      },
      ...getResetCodeMenuItems({
        t,
        isStudentTab: true,
        isCurrentAcademicYearSelected,
        isContextClassSettings: true,
        isFutureAcademicYearSelected,
        isFamilyInviteOptionsDisabled,
      }),
      {
        key: "divider-3",
        isDivider: true,
        visible:
          isStudentBlockingFeatureEnabled() &&
          !isPreviousAcademicYearSelected &&
          canResetStudentSignInAndFamilyInviteCodesInClassSettings(),
      },
      {
        key: "bulk_unblock_students",
        label: t("schoolSetup:unblock_student_access"),
        disabled: isUnblockStudentsDisabled,
        disabledTooltip: t("schoolSetup:no_students_to_unblock_tooltip"),
        visible:
          isStudentBlockingFeatureEnabled() &&
          !isPreviousAcademicYearSelected &&
          canResetStudentSignInAndFamilyInviteCodesInClassSettings(),
        icon: <DisableOutlined />,
      },
      {
        key: "bulk_block_students",
        label: t("schoolSetup:temporarily_block_student_access"),
        isDestructive: true,
        visible:
          isStudentBlockingFeatureEnabled() &&
          !isPreviousAcademicYearSelected &&
          canResetStudentSignInAndFamilyInviteCodesInClassSettings(),
        icon: <DisableOutlined />,
      },
      {
        label: t("schoolSetup:logout_user_from_all_devices", {
          label: _.toLower(t("common:student_plural")),
        }),
        key: "logout_students",
        isDestructive: true,
        icon: <LogoutOutlined />,
        visible:
          !isPreviousAcademicYearSelected &&
          canResetStudentSignInAndFamilyInviteCodesInClassSettings(),
      },
    ];
  } else {
    dropDownOptions = [
      {
        key: "students",
        label: t("common:student_plural"),
        isItemGroup: true,
        options: [
          {
            label: t("common:download_with_label", {
              label: t("common:student_template_data"),
            }),
            key: "dp_existing_students_download_template",
            visible: isDP && ACLStore.can("AdminPortal:RosterBulkUpload"),
            icon: <DownloadOutlined />,
          },
          {
            label: t("common:download_with_label", {
              label: t("common:list_of_all_students_in_xlsx"),
            }),
            visible: true,
            key: "student",
            icon: <DownloadOutlined />,
          },
          {
            label: t("journal:download_portfolio"),
            key: "student_portfolio_download",
            icon: <DownloadOutlined />,
            visible: ACLStore.can("FeatureFlag:BulkPortfolioDownload"),
            disabled: isPortfolioDownloadDisabled,
            disabledTooltip: t("journal:download_inprogress_please_try_later"),
          },
          {
            label: t(
              "academicSetup:download_student_profile_and_gradebook_summary"
            ),
            key: "student_profile_and_gradebook_summary",
            visible:
              selectedCurriculumProgramId &&
              isStudentProfileAndGradebookSummaryDownloadEnabled,
            icon: <DownloadOutlined />,
          },
          {
            key: "bulk_unblock_students",
            label: t("schoolSetup:unblock_student_access"),
            visible:
              isStudentBlockingFeatureEnabled() &&
              canResetStudentCodeAndFamilyInviteInSchoolRoster(
                selectedCurriculumProgramId
              ) &&
              (isCurrentAcademicYearSelected || isFutureAcademicYearSelected),
            icon: <DisableOutlined />,
          },
          {
            key: "bulk_block_students",
            label: t("schoolSetup:temporarily_block_student_access"),
            isDestructive: true,
            visible:
              isStudentBlockingFeatureEnabled() &&
              canResetStudentCodeAndFamilyInviteInSchoolRoster(
                selectedCurriculumProgramId
              ) &&
              (isCurrentAcademicYearSelected || isFutureAcademicYearSelected),
            icon: <DisableOutlined />,
          },
          ...getResetCodeMenuItems({
            t,
            isStudentTab: true,
            isCurrentAcademicYearSelected,
            isFutureAcademicYearSelected,
            isFamilyInviteOptionsDisabled,
          }),
        ],
      },
      {
        key: "family",
        label: t("common:family"),
        isItemGroup: true,
        options: [
          {
            label: t("common:invite_family_members_email"),
            key: "FAMILY_INVITES_BY_EMAIL",
            icon: <EnvelopePlainOutlined />,
            visible:
              canResetStudentCodeAndFamilyInviteInSchoolRoster(
                selectedCurriculumProgramId
              ) &&
              !disableEditForMapleBearChildOrg() &&
              (isCurrentAcademicYearSelected || isFutureAcademicYearSelected) &&
              !isFamilyInviteOptionsDisabled,
          },
          {
            label: t("common:print_with_label_in_pdf", {
              label: t("common:family_invites_for_each_student"),
            }),
            visible:
              !disableEditForMapleBearChildOrg() &&
              !isFamilyInviteOptionsDisabled,
            key: "PRINT_FAMILY_INVITE_FOR_STUDENTS",
            icon: <PrintOutlined />,
          },
          {
            label: t("common:download_with_label_in_xlsx", {
              label: t("common:student_list_of_unconnected_family"),
            }),
            key: "STUDENT_WITH_UNCONNECTED_FAMILY",
            icon: <DownloadOutlined />,
            visible: true,
          },
          {
            label: t("common:download_with_label_in_xlsx", {
              label: t("common:all_family_members_with_label", {
                label: t("common:all"),
              }),
            }),
            key: "all_family_members",
            visible: true,
            icon: <DownloadOutlined />,
          },
          {
            label: t("common:reset_with_label", {
              label: t("common:family_invite_code_plural"),
            }),
            key: "reset_family_code",
            visible:
              canResetStudentCodeAndFamilyInviteInSchoolRoster(
                selectedCurriculumProgramId
              ) &&
              !disableEditForMapleBearChildOrg() &&
              (isCurrentAcademicYearSelected || isFutureAcademicYearSelected) &&
              !isFamilyInviteOptionsDisabled,
            icon: <ReloadArrowOutlined />,
            isDestructive: true,
          },
        ],
      },
    ];
  }

  const filteredDropdownOptions = isContextClassSettings
    ? dropDownOptions.filter(item => item.visible)
    : dropDownOptions.map(item => ({
        ...item,
        options: item.options?.filter(option => option.visible) || [],
      }));

  return getFilteredDividers(filteredDropdownOptions);
};

export const getStudentDownloadDropDownOptions = ({
  t,
  isDP,
  isPortfolioDownloadDisabled,
  selectedCurriculumProgramId,
  isStudentProfileAndGradebookSummaryDownloadEnabled,
}) => {
  const dropDownOptions = [
    {
      key: "students",
      label: t("common:student_plural"),
      isItemGroup: true,
      options: [
        {
          label: t("common:download_with_label", {
            label: t("common:student_template_data"),
          }),
          key: "dp_existing_students_download_template",
          visible: isDP && ACLStore.can("AdminPortal:RosterBulkUpload"),
          icon: <DownloadOutlined />,
        },
        {
          label: t("common:download_with_label", {
            label: t("common:list_of_all_students_in_xlsx"),
          }),
          visible: true,
          key: "student",
          icon: <DownloadOutlined />,
        },
        {
          label: t("journal:download_portfolio"),
          key: "student_portfolio_download",
          icon: <DownloadOutlined />,
          visible: ACLStore.can("FeatureFlag:BulkPortfolioDownload"),
          disabled: isPortfolioDownloadDisabled,
          disabledTooltip: t("journal:download_inprogress_please_try_later"),
        },
        {
          label: t(
            "academicSetup:download_student_profile_and_gradebook_summary"
          ),
          key: "student_profile_and_gradebook_summary",
          visible:
            selectedCurriculumProgramId &&
            isStudentProfileAndGradebookSummaryDownloadEnabled,
          icon: <DownloadOutlined />,
        },
      ],
    },
    {
      key: "family",
      label: t("common:family"),
      isItemGroup: true,
      options: [
        {
          label: t("common:download_with_label_in_xlsx", {
            label: t("common:student_list_of_unconnected_family"),
          }),
          key: "STUDENT_WITH_UNCONNECTED_FAMILY",
          icon: <DownloadOutlined />,
          visible: true,
        },
        {
          label: t("common:download_with_label_in_xlsx", {
            label: t("common:all_family_members_with_label", {
              label: t("common:all"),
            }),
          }),
          key: "all_family_members",
          visible: true,
          icon: <DownloadOutlined />,
        },
      ],
    },
  ];

  const filteredDropdownOptions = _.map(dropDownOptions, item => ({
    ...item,
    options: _.filter(item.options, option => option.visible),
  }));

  return getFilteredDividers(filteredDropdownOptions);
};

export const getAdditionalStatusDropdownOptions = ({ t, isStudentTab }) => {
  const options = [
    {
      key: "BLOCKED",
      label: t("common:blocked"),
      isVisible: isStudentBlockingFeatureEnabled() && isStudentTab,
    },
  ];

  return _.filter(options, option => option.isVisible);
};

export const getRefetchQueriesForStudentBlock = ({ state }) => {
  const courseId = _.get(state, "teacher.selected_class.selected_course", null);
  const organizationId = _.get(state, "login.userInfo.org_id", null);
  if (courseId) {
    // Class-settings page
    const filters = _.get(state, "studentPortfolio.studentFilters", {});
    const staffRelationFilters = getStaffRelationFilters(state);
    const sortFields = _.get(state, "studentPortfolio.sortFields", {});
    const { orderBy: orderByAlias, orderByDirection } = sortFields;
    const orderBy = _.size(orderByAlias)
      ? orderByAlias
      : getDefaultOrderBy({ type: "student" });

    const loginActiveTab = _.get(state, "login.activeTab", "");
    const curriculumProgramId = getCurriculumProgramId(state);
    const selectedAcademicYear = getSelectedAcademicYear(state);
    const selectedAcademicYearId = _.get(selectedAcademicYear, "id", "");
    const archivalState = _.get(
      state,
      "studentPortfolio.studentFilters.archivalState",
      "ACTIVE"
    );

    const blockStatus = _.get(
      state,
      "studentPortfolio.studentFilters.blockStatus",
      "UNBLOCKED"
    );

    const userCourseMapFilters = getUserCourseMapFiltersV2({
      archivalState: "ALL",
    });

    const studentFeedFilters = getFamilyFeedQueryFilters({
      filters,
      loginActiveTab,
      courseId,
      curriculumProgramId,
      archivalState,
      academicYearId: selectedAcademicYearId,
      blockStatus,
    });

    const familiesFilter = getFamiliesFilters({ filters, loginActiveTab });

    return [
      {
        query: getCourseStudentPortfolioQuery,
        variables: getCourseStudentPortfolioVariables({
          courseId,
          studentFilters: filters,
          academicYearId: selectedAcademicYearId,
          staffRelationFilters,
          orderBy,
          orderByDirection,
        }),
      },
      // Refetch query for the connected Students Families Count
      {
        query: getStudentFeedQuery,
        variables: getStudentFeedQueryVariablesMemoized({
          organizationId,
          filters: studentFeedFilters,
          familyStatus: { archivalState },
          orderBy: "FIRST_NAME",
          orderByDirection: "ASC",
          familiesFilter,
          withStaffRelation: false,
          userCourseMapFilters,
          userArchivedFilters: {
            academicYearId: selectedAcademicYearId,
          },
          filtersWithoutSearch: {
            ...studentFeedFilters,
            searchText: "",
          },
        }),
      },
    ];
  } else {
    // Student page in school setup
    const sortedFieldsWithOrder = state.schoolSetup.sortFields.students;

    const { orderByDirection, orderBy } = getTableSortOrder({
      sortedFieldsWithOrder,
    });

    const filters = _.get(state, "studentSetup.filters", {});
    const selectedAcademicYearId = getSelectedAcademicYearId(state);

    const academicYearIds = selectedAcademicYearId
      ? [selectedAcademicYearId]
      : [];

    const userCourseMapFilters = getUserCourseMapFiltersV2({
      archivalState: "ALL",
      academicYearIds,
    });

    const staffFilter = getStaffFilter({
      id: getSelectedAcademicYearId(state),
    });

    const studentQueryFilters = getStudentFilters(state);
    return [
      {
        query: getStudentFeedQuery,
        variables: getStudentQueryVariables({
          organizationId,
          selectedAcademicYearId,
          userCourseMapFilters,
          orderByDirection,
          orderBy,
          staffFilter,
          studentQueryFilters: studentQueryFilters?.filters,
          academicYearIds: _.get(filters, "academicYears"),
          includeFiltersWithoutSearch: true,
        }),
      },
    ];
  }
};

export const getModalContent = ({
  type,
  title,
  firstName,
  t,
  isDeleteDialogChecked,
  toggleDeleteDialog,
  withdrawalDate = "",
  onWithdrawalDateChange = () => {},
  studentEnrollmentDate = "",
}) => {
  switch (type) {
    case "RESTORE": {
      return {
        modalTitle: t("schoolSetup:restore_with_label_v2", {
          label: t("schoolSetup:student_with_name", { name: title }),
        }),
        spanContent: (
          <Trans i18nKey={t("schoolSetup:restore_entity_confirmation_text")}>
            You are about to restore{" "}
            {{ userType: t("common:student_lowercase") }} ‘
            {{ entityLabel: title }}‘. Once restored, they will be automatically
            re-added to any classes they were part of before being archived.
            Would you like to continue?
          </Trans>
        ),
        alertText: null,
      };
    }

    case "ARCHIVE":
      return {
        modalTitle: t("schoolSetup:archive_student_with_label", {
          label: title,
        }),
        spanContent: (
          <Trans i18nKey="schoolSetup:archive_student_helper_text">
            You are archiving ‘{{ name: title }}’. ‘{{ firstName }}’ and their
            family would no longer be able to login to their accounts on Toddle.
            <br />
            <br />
            You will be able to undo this action and restore ‘{{ firstName }}’
            from the archive. Would you like to continue?
          </Trans>
        ),
        datePickerContent: (
          <>
            <span className="text-label">{t("common:withdrawal_date")}</span>
            <span className="text-body-s text-secondary">
              {t("schoolSetup:withdrawal_date_label_subtext", {
                count: 1,
              })}
            </span>

            <DatePicker
              picker="date"
              value={moment(withdrawalDate)}
              onChange={onWithdrawalDateChange}
              minDate={moment(studentEnrollmentDate)}
              maxDate={moment()}
              size="medium"
            />
          </>
        ),
        alertText: (
          <Alert
            message={
              <Trans i18nKey="schoolSetup:archive_student_alert_text">
                Teachers and students will not be able to tag{" "}
                {{ name: firstName }}
                in any new content (posts, submissions, etc.). <br />
                <br /> However, the student will not be untagged/removed from
                any previous content.
              </Trans>
            }
          />
        ),
      };

    case "DELETE":
      return {
        modalTitle: t("common:delete_with_label", {
          label: t("schoolSetup:student_with_name", { name: title }),
        }),
        spanContent: (
          <Trans i18nKey="schoolSetup:delete_type_confirmation_v2">
            Are you sure you want to delete{" "}
            {{ type: t("schoolSetup:student_with_name", { name: title }) }}?
          </Trans>
        ),
        alertText: (
          <Alert
            type={"error"}
            message={t("schoolSetup:delete_student_alert_text")}
            isChecked={isDeleteDialogChecked}
            onChange={toggleDeleteDialog}
            dsVersion={"2.0"}
          />
        ),
      };
  }
};

export const buildClassAndLevelsMap = classList =>
  _.reduce(
    classList,
    (acc, cls) => {
      acc[cls.id] = {
        level: _.get(cls, "level", []),
        subjects: _.map(_.get(cls, "subjects", []), "id"),
      };
      return acc;
    },
    {}
  );

const extractSubjectsWithLevels = c => {
  const levels = _.get(c, "levels", []);

  return _.map(levels, ({ levelId, subjectId }) => ({
    subjectId,
    levelId,
  }));
};

const buildAddedClassLevels = ({ updatedClassList, addedClassIds }) => {
  return _.map(
    _.filter(updatedClassList, cls => _.includes(addedClassIds, cls.id)),
    cls => ({
      classId: cls.id,
      subjectWiseLevels: extractSubjectsWithLevels(cls),
    })
  );
};

const buildRemovedClassLevels = ({
  originalStudentCourseMap,
  removedClassIds,
}) => {
  const removedClassesMap = _.filter(originalStudentCourseMap, ({ course }) =>
    _.includes(removedClassIds, course?.id)
  );

  return _.map(removedClassesMap, item => ({
    classId: item.course.id,
    subjectWiseLevels: _.map(
      _.get(item, "subjectWiseLevel", []),
      ({ level, subject }) => ({
        subjectId: subject.id,
        levelId: level.id,
      })
    ),
  }));
};

const buildEditedClassLevels = ({
  updatedClassList,
  originalStudentCourseMap,
  addedClassIds,
  removedClassIds,
}) => {
  const currentClasses = _.filter(
    updatedClassList,
    cls =>
      !_.includes(addedClassIds, cls.id) && !_.includes(removedClassIds, cls.id)
  );

  const originalClassesInMap = _.filter(
    originalStudentCourseMap,
    ({ course }) =>
      !_.includes(addedClassIds, course?.id) &&
      !_.includes(removedClassIds, course?.id)
  );

  const existingByClassId = {};
  const currentByClassId = {};

  _.forEach(originalClassesInMap, item => {
    existingByClassId[item.course.id] = {
      classId: item.course.id,
      subjectWiseLevels: _.map(
        _.get(item, "subjectWiseLevel", []),
        ({ level, subject }) => ({
          subjectId: subject.id,
          levelId: level.id,
        })
      ),
    };
  });

  _.forEach(currentClasses, item => {
    currentByClassId[item.id] = {
      classId: item.id,
      subjectWiseLevels: extractSubjectsWithLevels(item),
    };
  });

  const addedSubjectWiseLevels = [];
  const removedSubjectWiseLevels = [];

  const commonClassIds = _.intersection(
    _.keys(existingByClassId, "classId"),
    _.map(currentByClassId, "classId")
  );

  _.forEach(commonClassIds, classId => {
    const existing = existingByClassId[classId]?.subjectWiseLevels || [];
    const current = currentByClassId[classId]?.subjectWiseLevels || [];

    const currentKeys = new Set(
      current.map(c => `${c.subjectId}-${c.levelId}`)
    );

    const removed = existing.filter(
      e => !currentKeys.has(`${e.subjectId}-${e.levelId}`)
    );

    if (current.length > 0) {
      addedSubjectWiseLevels.push({
        classId,
        subjectWiseLevels: current,
      });
    }

    if (removed.length > 0) {
      removedSubjectWiseLevels.push({
        classId,
        subjectWiseLevels: removed,
      });
    }
  });

  return {
    addedSubjectWiseLevels,
    removedSubjectWiseLevels,
  };
};

export const buildStudentClassUpdatePayload = (
  originalStudentCourseMap,
  studentClassMapping
) => {
  const updatedClassList = _.get(studentClassMapping, "classList", []);

  const originalClassIds = _.map(originalStudentCourseMap, "course.id");
  const currentClassIds = _.map(updatedClassList, "id");

  /**
   * if levels are edited for an already mapped class to a student:
   * consider it as both added and removed, this is required for projects to work properly in the backend
   * */

  const levelsEditedClassesIds = _.intersection(
    _.map(_.filter(updatedClassList, { areLevelsEdited: true }), "id"),
    originalClassIds
  );

  const addedClassIds = _.difference(currentClassIds, originalClassIds);
  const removedClassIds = _.difference(originalClassIds, currentClassIds);

  const addedCourses = _.uniq([...addedClassIds, ...levelsEditedClassesIds]);
  const removedCourses = _.uniq([
    ...removedClassIds,
    ...levelsEditedClassesIds,
  ]);

  const addedClassLevelsMap = buildAddedClassLevels({
    updatedClassList,
    addedClassIds,
  });

  const removedClassLevelsMap = buildRemovedClassLevels({
    originalStudentCourseMap,
    removedClassIds,
  });

  const {
    addedSubjectWiseLevels,
    removedSubjectWiseLevels,
  } = buildEditedClassLevels({
    updatedClassList,
    originalStudentCourseMap,
    addedClassIds,
    removedClassIds,
  });

  return {
    addedClassLevelsMap: [...addedClassLevelsMap, ...addedSubjectWiseLevels],
    removedClassLevelsMap: [
      ...removedClassLevelsMap,
      ...removedSubjectWiseLevels,
    ],
    addedCourses,
    removedCourses,
  };
};

export const BULK_ACTIONS = {
  ADD_STUDENTS_TO_CLASS: "add_student_to_class",
  STUDENTS: "students",
  MAP_HOMEROOM_ADVISOR: "map_homeroom_advisor",
  PRINT_STUDENT_SIGN_IN_CODES: "print_student_sign_in_codes",
  DOWNLOAD_LIST_OF_SELECTED_STUDENTS: "download_list_of_selected_students",
  UNBLOCK_STUDENTS: "unblock_students",
  RESET_STUDENT_SIGN_IN_CODE: "reset_student_sign_in_code",
  BULK_BLOCK_STUDENTS: "bulk_block_students",
  LOGOUT_STUDENTS: "logout_students",
  ARCHIVE_STUDENT: "archive_student",
  RESTORE_STUDENT: "restore_student",
  FAMILY: "family",
  INVITE_FAMILY_MEMBERS_VIA_EMAIL: "invite_family_members_via_email",
  PRINT_FAMILY_INVITE: "print_family_invite",
  RESET_FAMILY_CODE: "reset_family_code",
  REMOVE_FAMILY_MEMBER: "remove_family_member",
  NO_ACTIONS_AVAILABLE: "no_actions_available",
};

export const getToolbarActionsDropdownOptions = ({
  t,
  isDP,
  isCrossYearGroupStudentsForDPEnabled,
  hasOnlyOneYearGroupStudentsSelected,
  isCurrentAcademicYearSelected,
  isFutureAcademicYearSelected,
  selectedCurriculumProgramId,
  isAllCurriculumProgramSelectedInAllStudents,
  areActiveStudentsSelected,
  areBlockedStudentsSelected,
  areArchivedStudentsSelected,
  isAllStatus,
  isActiveStatus,
  isBlockedStatus,
  isArchivedStatus,
  hasFamilyMembers,
  isFamilyInviteOptionsDisabled,
  isContextClassSettings = false,
}) => {
  const canResetCodes = isContextClassSettings
    ? canResetStudentSignInAndFamilyInviteCodesInClassSettings()
    : canResetStudentCodeAndFamilyInviteInSchoolRoster(
        selectedCurriculumProgramId
      );

  const canManageRoster = isContextClassSettings
    ? canManageClassSettings()
    : canManageSchoolRoster(selectedCurriculumProgramId);

  const canViewCodes = isContextClassSettings
    ? canViewStudentSignInAndFamilyInviteCodesInClassSettings()
    : true;

  const hasLimitedStudentActions =
    isBlockedStatus ||
    isArchivedStatus ||
    (isAllStatus && areArchivedStudentsSelected);

  const bulkActionVisibilityBasedOnStatus = {
    [BULK_ACTIONS.BULK_BLOCK_STUDENTS]:
      /**
       * "Temporarily Block Students" is only valid if active students are selected. However, when the
       * "Blocked" filter is applied, Redux sets archivalState = "ACTIVE" and
       * blockStatus = "BLOCKED". To avoid showing the action for already-blocked
       * students, we require both conditions: ACTIVE and NOT BLOCKED.
       */
      (isAllStatus &&
        areActiveStudentsSelected &&
        !areArchivedStudentsSelected) ||
      (isActiveStatus && !isBlockedStatus),

    [BULK_ACTIONS.UNBLOCK_STUDENTS]:
      isBlockedStatus ||
      (isAllStatus &&
        areBlockedStudentsSelected &&
        !areArchivedStudentsSelected),

    [BULK_ACTIONS.ARCHIVE_STUDENT]:
      isActiveStatus ||
      isBlockedStatus ||
      (isAllStatus && !areArchivedStudentsSelected),

    [BULK_ACTIONS.RESTORE_STUDENT]:
      (isAllStatus &&
        areArchivedStudentsSelected &&
        !areActiveStudentsSelected &&
        !areBlockedStudentsSelected) ||
      isArchivedStatus,

    [BULK_ACTIONS.MAP_HOMEROOM_ADVISOR]:
      !isAllCurriculumProgramSelectedInAllStudents &&
      !isArchivedStatus &&
      !areArchivedStudentsSelected,

    [BULK_ACTIONS.PRINT_STUDENT_SIGN_IN_CODES]:
      !isArchivedStatus && !areArchivedStudentsSelected,

    [BULK_ACTIONS.RESET_STUDENT_SIGN_IN_CODE]:
      !isArchivedStatus && !areArchivedStudentsSelected,

    [BULK_ACTIONS.LOGOUT_STUDENTS]:
      !isArchivedStatus && !isBlockedStatus && !areArchivedStudentsSelected,

    [BULK_ACTIONS.INVITE_FAMILY_MEMBERS_VIA_EMAIL]:
      !isArchivedStatus && !areArchivedStudentsSelected,

    [BULK_ACTIONS.PRINT_FAMILY_INVITE]:
      !isArchivedStatus && !areArchivedStudentsSelected,

    [BULK_ACTIONS.RESET_FAMILY_CODE]:
      !isArchivedStatus && !areArchivedStudentsSelected,

    [BULK_ACTIONS.REMOVE_FAMILY_MEMBER]:
      !isArchivedStatus && !areArchivedStudentsSelected,

    [BULK_ACTIONS.ADD_STUDENTS_TO_CLASS]:
      !isArchivedStatus && !areArchivedStudentsSelected,
  };

  const isArchiveOptionVisible =
    bulkActionVisibilityBasedOnStatus[BULK_ACTIONS.ARCHIVE_STUDENT] &&
    !disableEditForMapleBearChildOrg() &&
    canManageRoster &&
    !isContextClassSettings &&
    (isUserArchivalAcademicYearWiseEnabled()
      ? true
      : isCurrentAcademicYearSelected);

  const isRestoreFeatureVisible =
    bulkActionVisibilityBasedOnStatus[BULK_ACTIONS.RESTORE_STUDENT] &&
    !disableEditForMapleBearChildOrg() &&
    canManageRoster &&
    !isContextClassSettings &&
    (isUserArchivalAcademicYearWiseEnabled()
      ? true
      : isCurrentAcademicYearSelected);

  const isAddStudentsToClassVisible = false;
  // bulkActionVisibilityBasedOnStatus[BULK_ACTIONS.ADD_STUDENTS_TO_CLASS] &&
  //   canManageSchoolRoster(selectedCurriculumProgramId) &&
  //   !isAllCurriculumProgramSelectedInAllStudents;

  const isAddStudentsToClassDisabled =
    isDP &&
    !isCrossYearGroupStudentsForDPEnabled &&
    !hasOnlyOneYearGroupStudentsSelected;

  const allActionGroups = [
    {
      key: BULK_ACTIONS.STUDENTS,
      label: t("common:student"),
      ...(hasLimitedStudentActions && {
        subText: t(
          "schoolSetup:limited_bulk_actions_availability_status_message",
          {
            status: t("schoolSetup:archived_or_blocked"),
          }
        ),
      }),
      isItemGroup: true,
      options: [
        {
          key: BULK_ACTIONS.ADD_STUDENTS_TO_CLASS,
          label: t("schoolSetup:add_to_classes"),
          icon: <StudentAddOutlined />,
          visible: isAddStudentsToClassVisible,
          disabled: isAddStudentsToClassDisabled,
          disabledTooltip: t(
            "schoolSetup:dp1_and_dp2_students_selected_tooltip"
          ),
        },
        {
          key: BULK_ACTIONS.MAP_HOMEROOM_ADVISOR,
          label: t("schoolSetup:map_homeroom_advisor"),
          icon: <ManageMembersOutlined />,
          visible:
            !isContextClassSettings &&
            isHomeroomEnabled() &&
            canManageRoster &&
            bulkActionVisibilityBasedOnStatus[
              BULK_ACTIONS.MAP_HOMEROOM_ADVISOR
            ],
        },
        {
          key: BULK_ACTIONS.DOWNLOAD_LIST_OF_SELECTED_STUDENTS,
          label: t("common:download_list_of_selected_entity_xlsx", {
            entity: t("common:students"),
          }),
          icon: <DownloadOutlined />,
          visible: true,
        },
        {
          key: BULK_ACTIONS.PRINT_STUDENT_SIGN_IN_CODES,
          label: t("common:print_with_label_in_pdf", {
            label: t("common:student_sign_in_code_plural"),
          }),
          icon: <PrintOutlined />,
          visible:
            canViewCodes &&
            !disableEditForMapleBearChildOrg() &&
            bulkActionVisibilityBasedOnStatus[
              BULK_ACTIONS.PRINT_STUDENT_SIGN_IN_CODES
            ],
        },
        {
          key: BULK_ACTIONS.UNBLOCK_STUDENTS,
          label: t("schoolSetup:unblock_student_access"),
          icon: <DisableOutlined />,
          visible:
            isStudentBlockingFeatureEnabled() &&
            canResetCodes &&
            (isCurrentAcademicYearSelected || isFutureAcademicYearSelected) &&
            bulkActionVisibilityBasedOnStatus[BULK_ACTIONS.UNBLOCK_STUDENTS],
        },
        {
          key: BULK_ACTIONS.RESTORE_STUDENT,
          label: t("common:restore_label", {
            label: t("common:students"),
          }),
          icon: <ArchiveOutlined />,
          visible: isRestoreFeatureVisible,
        },
        {
          key: BULK_ACTIONS.RESET_STUDENT_SIGN_IN_CODE,
          label: t("common:reset_with_label", {
            label: _.lowerCase(t("common:student_sign_in_code_plural")),
          }),
          icon: <ReloadArrowOutlined />,
          isDestructive: true,
          visible:
            canResetCodes &&
            !disableEditForMapleBearChildOrg() &&
            (isCurrentAcademicYearSelected || isFutureAcademicYearSelected) &&
            bulkActionVisibilityBasedOnStatus[
              BULK_ACTIONS.RESET_STUDENT_SIGN_IN_CODE
            ],
        },
        {
          key: BULK_ACTIONS.BULK_BLOCK_STUDENTS,
          label: t("schoolSetup:temporarily_block_student_access"),
          icon: <DisableOutlined />,
          isDestructive: true,
          visible:
            isStudentBlockingFeatureEnabled() &&
            canResetCodes &&
            (isCurrentAcademicYearSelected || isFutureAcademicYearSelected) &&
            bulkActionVisibilityBasedOnStatus[BULK_ACTIONS.BULK_BLOCK_STUDENTS],
        },
        {
          key: BULK_ACTIONS.LOGOUT_STUDENTS,
          label: t("schoolSetup:logout_user_from_all_devices", {
            label: _.toLower(t("common:student_plural")),
          }),
          icon: <LogoutOutlined />,
          isDestructive: true,
          visible:
            canResetCodes &&
            (isCurrentAcademicYearSelected || isFutureAcademicYearSelected) &&
            bulkActionVisibilityBasedOnStatus[BULK_ACTIONS.LOGOUT_STUDENTS],
        },
        {
          key: BULK_ACTIONS.ARCHIVE_STUDENT,
          label: t("common:archive_label", {
            label: t("common:students"),
          }),
          icon: <ArchiveOutlined />,
          isDestructive: true,
          visible: isArchiveOptionVisible,
        },
      ],
    },
    {
      key: BULK_ACTIONS.FAMILY,
      label: t("common:family"),
      isItemGroup: true,
      options: [
        {
          key: BULK_ACTIONS.INVITE_FAMILY_MEMBERS_VIA_EMAIL,
          label: t("common:invite_family_members_email"),
          icon: <EnvelopePlainOutlined />,
          visible:
            (isContextClassSettings ? canManageRoster : canResetCodes) &&
            !disableEditForMapleBearChildOrg() &&
            (isCurrentAcademicYearSelected || isFutureAcademicYearSelected) &&
            bulkActionVisibilityBasedOnStatus[
              BULK_ACTIONS.INVITE_FAMILY_MEMBERS_VIA_EMAIL
            ],
        },
        {
          key: BULK_ACTIONS.PRINT_FAMILY_INVITE,
          label: t("common:print_with_label_in_pdf", {
            label: t("common:family_invite"),
          }),
          icon: <PrintOutlined />,
          visible:
            canViewCodes &&
            !disableEditForMapleBearChildOrg() &&
            !isFamilyInviteOptionsDisabled &&
            bulkActionVisibilityBasedOnStatus[BULK_ACTIONS.PRINT_FAMILY_INVITE],
        },
        {
          key: BULK_ACTIONS.RESET_FAMILY_CODE,
          label: t("common:reset_with_label", {
            label: t("common:family_invite_code_plural"),
          }),
          icon: <ReloadArrowOutlined />,
          isDestructive: true,
          visible:
            canResetCodes &&
            !disableEditForMapleBearChildOrg() &&
            (isCurrentAcademicYearSelected || isFutureAcademicYearSelected) &&
            bulkActionVisibilityBasedOnStatus[BULK_ACTIONS.RESET_FAMILY_CODE],
        },
        {
          key: BULK_ACTIONS.REMOVE_FAMILY_MEMBER,
          label: t("common:remove_with_label", {
            label: t("common:family_member"),
          }),
          icon: <MinusCircleOutlined />,
          isDestructive: true,
          visible:
            canManageRoster &&
            hasFamilyMembers &&
            bulkActionVisibilityBasedOnStatus[
              BULK_ACTIONS.REMOVE_FAMILY_MEMBER
            ],
        },
      ],
    },
  ];

  const visibleActionGroups = _.filter(
    _.map(allActionGroups, group => ({
      ...group,
      options: _.filter(group.options, action =>
        _.get(action, "visible", false)
      ),
    })),
    group => !_.isEmpty(group.options)
  );

  return visibleActionGroups;
};

export const getStudentSelectionStatus = ({
  studentFilters,
  bulkSelectedStudentStatusCounts,
  students,
  areAllStudentsSelected,
  selectedOrExcludedStudents,
  totalCountWithoutSearchFilter,
}) => {
  const { archivalState, blockStatus } = studentFilters;
  // 'archived' and 'blocked' counts appear only when the selected students include archived or blocked users
  const { archived, blocked } = bulkSelectedStudentStatusCounts;
  const selectedOrExcludedStudentsCount = _.size(selectedOrExcludedStudents);

  const isAllStatus = archivalState === "ALL";
  const isBlockedStatus = blockStatus === "BLOCKED";
  const isArchivedStatus = archivalState === "ARCHIVED";
  const isActiveStatus = archivalState === "ACTIVE";

  let activeStudentsCount = areAllStudentsSelected
    ? totalCountWithoutSearchFilter - selectedOrExcludedStudentsCount
    : selectedOrExcludedStudentsCount;

  // When status = "ALL", then need to remove the archived and blocked count
  if (isAllStatus) {
    activeStudentsCount = activeStudentsCount - archived - blocked;
  } else if (isBlockedStatus || isArchivedStatus) {
    activeStudentsCount = 0;
  }

  const areArchivedStudentsSelected = isAllStatus && archived > 0;
  const areBlockedStudentsSelected = isAllStatus && blocked > 0;
  const areActiveStudentsSelected = isAllStatus && activeStudentsCount > 0;
  // Flag to check if any of the selected students have family members
  const studentEdges = _.get(students, "edges", []);
  const hasFamilyMembers = areAllStudentsSelected
    ? _.some(studentEdges, s => !_.isEmpty(_.get(s, "node.families")))
    : _.some(
        studentEdges,
        s =>
          _.includes(selectedOrExcludedStudents, _.get(s, "node.id")) &&
          !_.isEmpty(_.get(s, "node.families"))
      );

  return {
    isAllStatus,
    isBlockedStatus,
    isArchivedStatus,
    isActiveStatus,
    areArchivedStudentsSelected,
    areBlockedStudentsSelected,
    areActiveStudentsSelected,
    hasFamilyMembers,
  };
};

export const getIsAllStudentsRoute = ({ state }) => {
  const activeTab = _.get(state, "login.activeTab", "");

  const { subChildId } = _.get(state, "administrator.routes", EMPTY_OBJECT);

  const adminNavigationChildId = _.get(state, "adminNavigation.childId", "");

  const isNewAdminNavigation = _.includes(
    ["schoolSettingsTab", "accountSettingsTab"],
    activeTab
  );

  return isNewAdminNavigation
    ? _.includes(["ALL_STUDENTS", "ALL_STUDENTS_V1"], adminNavigationChildId)
    : _.isEqual(subChildId, "ALL_STUDENTS");
};

export const getHomeRoomAdvisorOptions = (staffEdges = []) => {
  return _.map(staffEdges, edge => {
    const item = _.get(edge, "node", {});
    const fullName = getFormattedUserName({ user: item });
    const profileImage = _.get(item, "profileImage", "");
    const isArchived = _.get(item, "isArchived", false);

    const avatarColor = isArchived
      ? "neutral"
      : getDataGridRandomAvatarColor(item?.id);

    return {
      id: item?.id,
      label: fullName,
      isArchived,
      prefix: {
        type: "avatar",
        attrs: {
          src: profileImage,
          color: avatarColor,
          name: getInitials({ value: fullName }),
        },
      },
    };
  });
};
